import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  channelsService,
  type ChannelPayoutRate,
  type ChannelPayoutOverride,
  type CreateChannelPayload,
  type CreateChannelTypePayload,
  type UpdateChannelPayload,
} from '@/services/channels';
import { getErrorMessage } from '@/services';

/** `type` is a raw Channel.TYPE_CHOICES value and is applied SERVER-SIDE,
 *  before pagination -- see `channelsService.getUnifiedChannels`. It is part of
 *  the query key, so narrowing the type refetches rather than re-filtering
 *  whatever page happens to be cached. */
export function useUnifiedChannels(page = 1, search?: string, type?: string) {
  return useQuery({
    queryKey: ['unifiedChannels', page, search, type],
    queryFn: () => channelsService.getUnifiedChannels(page, { search, type }),
    placeholderData: (prev) => prev,
  });
}

/** ONE unified row, resolved by the channel id in the URL.
 *
 *  The detail page used to call `useUnifiedChannels(1)` and scan the results
 *  for its id. Page 1 holds 20 rows, so a broker with 30 agents could not open
 *  the last ten: the redirect landed correctly, every request answered 200, and
 *  the page said "Channel not found". Asking the server for the row removes the
 *  cliff instead of moving it to a larger page size.
 *
 *  The id in the URL is a CHANNEL id, and a POSP-backed channel surfaces as a
 *  `posp` row carrying `channel_id`, so the server matches either column. */
export function useUnifiedChannel(channelId: string | undefined) {
  return useQuery({
    queryKey: ['unifiedChannel', channelId],
    queryFn: () => channelsService.getUnifiedChannels(1, { id: channelId }),
    enabled: Boolean(channelId),
  });
}

// ── Channel types ──
/** Board #267: the agents eligible for the "Linked Agent / POSP" select --
 *  `entity_kind === 'posp'` rows with NO channel record (`channel_id === null`).
 *  An agent is in that state exactly when its owning channel was soft-deleted,
 *  which is also the state the channels list refuses to edit ("This POSP has no
 *  channel record and cannot be edited here"). One page at the server's maximum
 *  size: a broker with more unlinked agents than that should fix them in bulk,
 *  not through this modal.
 */
export function useUnlinkedPospOptions(enabled = true) {
  return useQuery({
    queryKey: ['unifiedChannels', 'unlinkedPosps'],
    queryFn: () => channelsService.getUnifiedChannels(1, { page_size: 100 }),
    enabled,
    select: (data) =>
      data.results.filter((r) => r.entity_kind === 'posp' && r.channel_id === null),
  });
}

/** The Manage Channel Types page: every channel under the type it belongs to. */
export function useChannelTypeGroups() {
  return useQuery({
    queryKey: ['channelTypeGroups'],
    queryFn: () => channelsService.getChannelTypeGroups(),
  });
}

export function useChannelTypes(page = 1) {
  return useQuery({
    queryKey: ['channelTypes', page],
    queryFn: () => channelsService.getChannelTypes(page),
    placeholderData: (prev) => prev,
  });
}

/** Every ACTIVE channel type, unpaginated, for the Add-Channel dropdown.
 *
 *  Keyed under ['channelTypes', …] on purpose: `useCreateChannelType` and
 *  `useDeleteChannelType` invalidate the ['channelTypes'] prefix, so a type
 *  created on the Channel Types page shows up in the picker without a reload.
 */
export function useActiveChannelTypes() {
  return useQuery({
    queryKey: ['channelTypes', 'active'],
    queryFn: () => channelsService.getChannelTypes(1, { is_active: true, page_size: 100 }),
    staleTime: 30_000,
  });
}

export function useCreateChannelType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateChannelTypePayload) => channelsService.createChannelType(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channelTypes'] });
      // The Channel Types page reads ['channelTypeGroups']; without this the
      // POST/DELETE succeeded, the toast said so, and the page did not change.
      qc.invalidateQueries({ queryKey: ['channelTypeGroups'] });
      toast.success('Channel type created');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useDeleteChannelType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => channelsService.deleteChannelType(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channelTypes'] });
      // The Channel Types page reads ['channelTypeGroups']; without this the
      // POST/DELETE succeeded, the toast said so, and the page did not change.
      qc.invalidateQueries({ queryKey: ['channelTypeGroups'] });
      toast.success('Channel type deleted');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateChannelPayload) => channelsService.createChannel(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unifiedChannels'] });
      qc.invalidateQueries({ queryKey: ['channelTypes'] });
      // The Channel Types page reads ['channelTypeGroups']; without this the
      // POST/DELETE succeeded, the toast said so, and the page did not change.
      qc.invalidateQueries({ queryKey: ['channelTypeGroups'] });
      toast.success('Channel added');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

/** Edit an existing channel. `channelId` must be the CHANNEL id (channelIdOf(row)).
 *
 *  A POSP edit also moves the POSP's login email/phone and payout % server-side,
 *  so the POSP caches are invalidated too — otherwise the agent's own detail page
 *  keeps showing the stale contact details.
 */
export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, data }: { channelId: string; data: UpdateChannelPayload }) =>
      channelsService.updateChannel(channelId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['unifiedChannels'] });
      qc.invalidateQueries({ queryKey: ['channelTypes'] });
      // The Channel Types page reads ['channelTypeGroups']; without this the
      // POST/DELETE succeeded, the toast said so, and the page did not change.
      qc.invalidateQueries({ queryKey: ['channelTypeGroups'] });
      qc.invalidateQueries({ queryKey: ['posps'] });
      qc.invalidateQueries({ queryKey: ['posp'] });
      toast.success('Channel updated');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ── Payout grid per channel type ──
export function useChannelPayoutRates(channelTypeId?: string) {
  return useQuery({
    queryKey: ['channelPayoutRates', channelTypeId],
    queryFn: () => channelsService.getChannelPayoutRates(channelTypeId!),
    enabled: !!channelTypeId,
  });
}

export function useCreateChannelPayoutRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ChannelPayoutRate>) => channelsService.createChannelPayoutRate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channelPayoutRates'] });
      qc.invalidateQueries({ queryKey: ['channelTypeGroups'] });
      qc.invalidateQueries({ queryKey: ['channelTypes'] });
      // The Channel Types page reads ['channelTypeGroups']; without this the
      // POST/DELETE succeeded, the toast said so, and the page did not change.
      qc.invalidateQueries({ queryKey: ['channelTypeGroups'] });
      toast.success('Payout rate added');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useDeleteChannelPayoutRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => channelsService.deleteChannelPayoutRate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channelPayoutRates'] });
      qc.invalidateQueries({ queryKey: ['channelTypeGroups'] });
      toast.success('Payout rate removed');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

// ── Per-channel payout override ──
export function useChannelPayoutOverrides(channelId?: string) {
  return useQuery({
    queryKey: ['channelPayoutOverrides', channelId],
    queryFn: () => channelsService.getChannelPayoutOverrides(channelId!),
    enabled: !!channelId,
  });
}

export function useCreateChannelPayoutOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ChannelPayoutOverride>) =>
      channelsService.createChannelPayoutOverride(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channelPayoutOverrides'] });
      toast.success('Override saved');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}

export function useDeleteChannelPayoutOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => channelsService.deleteChannelPayoutOverride(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channelPayoutOverrides'] });
      toast.success('Override removed');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
}
